import { useCallback, useMemo } from 'react'
import { Spinner, Stack, Tab } from 'react-bootstrap'
import { useParams } from 'react-router-dom'

import styled from 'styled-components'
import { BooleanParam, useQueryParam, withDefault } from 'use-query-params'

import { useTitle } from '@lib/web'
import * as Queries from '@piv/lib/rest-queries'
import { Button } from '@piv/portal/primitives/Buttons'
import { DataFromDate } from '@piv/portal/primitives/DataFromDate'
import {
  DataPackageList,
  DataPackageRow,
} from '@piv/portal/primitives/DataPackageRow'
import { InlineError } from '@piv/portal/primitives/InlineError'
import { InventoryLogo } from '@piv/portal/primitives/InventoryLogo'
import {
  FiltersTitle,
  SearchTitle,
} from '@piv/portal/primitives/InventoryTabTitles'
import { Panel } from '@piv/portal/primitives/Panel'
import { Tabs } from '@piv/portal/primitives/Tabs'

import { InventoryDetailMemo } from './InventoryDetail'
import { InventoryFilters } from './InventoryFilters'
import { InventoryRecordSearch } from './InventoryRecordSearch'
import { SelectPackagePrompt } from './SelectPackagePrompt'
import { useInventoryFilterParams } from './useInventoryFilterParams'
import { checkValidTab, useInventoryFilterTab } from './useInventoryFilterTab'

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: row;
`

const NoFiltersPanelWrapper = styled.div`
  width: 100%;
  height: 100%;
  flex-direction: column;
  display: flex;
  align-items: center;
  justify-content: center;
`

const CountTitle = styled.p`
  margin-bottom: 0.5rem;
`
const MyViewAllParam = withDefault(BooleanParam, false)

export function Inventory() {
  useTitle('Explore')
  const { filterTab, setFilterTab } = useInventoryFilterTab()

  const [viewAll, setViewAll] = useQueryParam('view-all', MyViewAllParam)

  const { id } = useParams<{ id?: string }>()
  const { filters, queryString, isAnyFilterSet } = useInventoryFilterParams()
  const filtersQuery = Queries.DataPackages.useFilters(filters)

  const dataPackages = Queries.DataPackages.useDataPackages(
    filters,
    queryString
  )
  const selectedPackageObj = useMemo(() => {
    if (!dataPackages.data?.packages) {
      return null
    }

    return (
      dataPackages.data?.packages.find(
        (dataPackage) => dataPackage.dataPackage.key === id
      ) ?? null
    )
  }, [dataPackages.data?.packages, id])

  const shouldShowPackages = isAnyFilterSet || queryString || viewAll

  const tabSelected = useCallback(
    (key: string | null) => {
      if (!checkValidTab(key)) {
        setFilterTab('filters')
        return
      }

      setFilterTab(key)
    },
    [setFilterTab]
  )

  return (
    <Container>
      <Panel width="19rem" shade="Dark" zIndex={2}>
        <Stack gap={3}>
          <InventoryLogo />
          <Tabs fill activeKey={filterTab as string} onSelect={tabSelected}>
            <Tab eventKey="filters" title={<FiltersTitle />} id="test">
              <InventoryFilters />
            </Tab>
            <Tab eventKey="record-search" title={<SearchTitle />}>
              <InventoryRecordSearch />
            </Tab>
          </Tabs>
        </Stack>
      </Panel>

      <Panel width="25rem" shade="Mid" zIndex={1}>
        {shouldShowPackages && (
          <>
            <div>
              {dataPackages.isFetching ? (
                <Spinner animation="border" size="sm" as="p" />
              ) : (
                <CountTitle>
                  {dataPackages.data?.packages.length ?? 0} data packages found
                </CountTitle>
              )}
            </div>

            <InlineError
              isError={filtersQuery.isError || dataPackages.isError}
            />

            <DataPackageList>
              {dataPackages.data?.packages.map((dataPackage) => {
                return (
                  <DataPackageRow
                    key={dataPackage.dataPackage.key}
                    title={dataPackage.dataPackage.displayName}
                    subtitle={
                      <DataFromDate
                        prefix="Data back to"
                        date={dataPackage.dateFrom}
                        renderDataWarning={(date) => date >= new Date(2020, 0)}
                      />
                    }
                    dataPackageKey={dataPackage.dataPackage.key}
                  />
                )
              })}
            </DataPackageList>
          </>
        )}

        {!shouldShowPackages && (
          <NoFiltersPanelWrapper>
            <h3>
              {dataPackages.data?.packages.length ?? 0} data packages found
            </h3>
            <p>Select filters or</p>
            <Button onClick={() => setViewAll(true)}>View all</Button>
          </NoFiltersPanelWrapper>
        )}
      </Panel>

      {selectedPackageObj ? (
        <InventoryDetailMemo dataPackage={selectedPackageObj} />
      ) : (
        <SelectPackagePrompt />
      )}
    </Container>
  )
}
